import { CommonModule } from "@angular/common";
import { Component, DestroyRef, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { forkJoin, of } from "rxjs";
import {
  EzConfigurationResolutionResult,
  EzFieldMetadataCatalog,
  EzIssueCatalog,
  EzLookupCatalog,
  EzRuleBindingCatalog,
  EzTerminologyHealthReport,
  PlatformConfigurationDiagnosticResult,
  PlatformConfigurationDiagnosticScopeQuery,
  PlatformConfigurationFieldMetadataRequest,
  PlatformConfigurationLookupCatalogRequest,
  PlatformConfigurationRuleBindingsRequest,
  PlatformConfigurationTerminologyHealthRequest,
} from "./platform-configuration-diagnostics.models";
import { PlatformConfigurationDiagnosticsService } from "./platform-configuration-diagnostics.service";

@Component({
  selector: "phms-platform-configuration-diagnostics",
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: "./platform-configuration-diagnostics.component.html",
  styleUrl: "./platform-configuration-diagnostics.component.scss",
})
export class PlatformConfigurationDiagnosticsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly diagnosticsService = inject(PlatformConfigurationDiagnosticsService);

  readonly diagnosticsForm = this.formBuilder.nonNullable.group({
    configurationCode: ["phms-runtime", [Validators.required]],
    scopeTypeCode: ["jurisdiction-program"],
    scopeKey: ["ca/phms"],
    asOfUtc: [""],
    domainCode: ["phms-readiness-status"],
    entityCode: ["phms-scheduling"],
    subjectCode: ["phms-scheduling"],
    bindingTypeCode: [""],
    localeCode: ["en-us", [Validators.required]],
    channelCode: [""],
    audienceCode: [""],
    tenantId: [""],
  });

  readonly isLoading = signal(false);
  readonly resolutionResult = signal<PlatformConfigurationDiagnosticResult<EzConfigurationResolutionResult> | null>(null);
  readonly lookupResult = signal<PlatformConfigurationDiagnosticResult<EzLookupCatalog> | null>(null);
  readonly fieldMetadataResult = signal<PlatformConfigurationDiagnosticResult<EzFieldMetadataCatalog> | null>(null);
  readonly issueCatalogResult = signal<PlatformConfigurationDiagnosticResult<EzIssueCatalog> | null>(null);
  readonly ruleBindingsResult = signal<PlatformConfigurationDiagnosticResult<EzRuleBindingCatalog> | null>(null);
  readonly terminologyHealthResult = signal<PlatformConfigurationDiagnosticResult<EzTerminologyHealthReport> | null>(null);

  readonly hasAnyResult = computed(() =>
    this.resolutionResult() !== null
    || this.lookupResult() !== null
    || this.fieldMetadataResult() !== null
    || this.issueCatalogResult() !== null
    || this.ruleBindingsResult() !== null
    || this.terminologyHealthResult() !== null,
  );

  readonly resolutionTrace = computed(() => this.resolutionResult()?.data?.trace ?? []);
  readonly lookupValues = computed(() => this.lookupResult()?.data?.values ?? []);
  readonly fieldMetadata = computed(() => this.fieldMetadataResult()?.data?.fields ?? []);
  readonly issueEntries = computed(() => this.issueCatalogResult()?.data?.entries ?? []);
  readonly ruleBindingEntries = computed(() => this.ruleBindingsResult()?.data?.entries ?? []);
  readonly terminologyHealthDiagnostics = computed(() => this.terminologyHealthResult()?.data?.diagnostics ?? []);
  readonly terminologyUsageEntries = computed(() =>
    this.terminologyHealthResult()?.data?.usageEvidence?.entries ?? [],
  );

  runDiagnostics(): void {
    if (this.diagnosticsForm.invalid) {
      this.diagnosticsForm.markAllAsTouched();
      return;
    }

    const request = this.createScopeRequest();
    this.clearResults();
    this.isLoading.set(true);

    this.diagnosticsService.resolveConfiguration(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resolution) => {
        this.resolutionResult.set(resolution);
        if (resolution.state !== "resolved") {
          this.isLoading.set(false);
          return;
        }

        const lookupRequest = this.createLookupRequest(request);
        const fieldMetadataRequest = this.createFieldMetadataRequest(request);

        forkJoin({
          lookup: lookupRequest
            ? this.diagnosticsService.getLookupCatalog(lookupRequest)
            : of(null),
          fieldMetadata: fieldMetadataRequest
            ? this.diagnosticsService.getFieldMetadataCatalog(fieldMetadataRequest)
            : of(null),
          issueCatalog: this.diagnosticsService.getIssueCatalog(request),
          ruleBindings: this.diagnosticsService.getRuleBindings(this.createRuleBindingsRequest(request)),
          terminologyHealth: this.diagnosticsService.getTerminologyHealth(
            this.createTerminologyHealthRequest(request),
          ),
        })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((results) => {
            this.lookupResult.set(results.lookup);
            this.fieldMetadataResult.set(results.fieldMetadata);
            this.issueCatalogResult.set(results.issueCatalog);
            this.ruleBindingsResult.set(results.ruleBindings);
            this.terminologyHealthResult.set(results.terminologyHealth);
            this.isLoading.set(false);
          });
      });
  }

  resetDiagnostics(): void {
    this.diagnosticsForm.reset({
      configurationCode: "phms-runtime",
      scopeTypeCode: "jurisdiction-program",
      scopeKey: "ca/phms",
      asOfUtc: "",
      domainCode: "phms-readiness-status",
      entityCode: "phms-scheduling",
      subjectCode: "phms-scheduling",
      bindingTypeCode: "",
      localeCode: "en-us",
      channelCode: "",
      audienceCode: "",
      tenantId: "",
    });
    this.clearResults();
    this.isLoading.set(false);
  }

  formatMessage(result: PlatformConfigurationDiagnosticResult<unknown> | null): string {
    if (!result) {
      return "";
    }

    if (result.state === "resolved") {
      return "Resolved";
    }

    return result.error?.messages.join(" ") ?? "Configuration diagnostics unavailable.";
  }

  formatCacheAge(resolvedAtUtc: string): string {
    const resolvedAt = Date.parse(resolvedAtUtc);
    if (!Number.isFinite(resolvedAt)) {
      return "unknown";
    }

    const ageSeconds = Math.max(0, Math.floor((Date.now() - resolvedAt) / 1_000));
    return `${ageSeconds} second${ageSeconds === 1 ? "" : "s"}`;
  }

  private clearResults(): void {
    this.resolutionResult.set(null);
    this.lookupResult.set(null);
    this.fieldMetadataResult.set(null);
    this.issueCatalogResult.set(null);
    this.ruleBindingsResult.set(null);
    this.terminologyHealthResult.set(null);
  }

  private createScopeRequest(): PlatformConfigurationDiagnosticScopeQuery {
    const value = this.diagnosticsForm.getRawValue();
    return {
      configurationCode: value.configurationCode.trim(),
      scopeTypeCode: normalizeOptionalText(value.scopeTypeCode),
      scopeKey: normalizeOptionalText(value.scopeKey),
      asOfUtc: normalizeOptionalText(value.asOfUtc),
    };
  }

  private createLookupRequest(
    request: PlatformConfigurationDiagnosticScopeQuery,
  ): PlatformConfigurationLookupCatalogRequest | null {
    const domainCode = normalizeOptionalText(this.diagnosticsForm.controls.domainCode.value);
    return domainCode
      ? { ...request, domainCode }
      : null;
  }

  private createFieldMetadataRequest(
    request: PlatformConfigurationDiagnosticScopeQuery,
  ): PlatformConfigurationFieldMetadataRequest | null {
    const entityCode = normalizeOptionalText(this.diagnosticsForm.controls.entityCode.value);
    return entityCode
      ? { ...request, entityCode }
      : null;
  }

  private createRuleBindingsRequest(
    request: PlatformConfigurationDiagnosticScopeQuery,
  ): PlatformConfigurationRuleBindingsRequest {
    return {
      ...request,
      subjectCode: normalizeOptionalText(this.diagnosticsForm.controls.subjectCode.value),
      bindingTypeCode: normalizeOptionalText(this.diagnosticsForm.controls.bindingTypeCode.value),
    };
  }

  private createTerminologyHealthRequest(
    request: PlatformConfigurationDiagnosticScopeQuery,
  ): PlatformConfigurationTerminologyHealthRequest {
    return {
      ...request,
      localeCode: this.diagnosticsForm.controls.localeCode.value.trim(),
      channelCode: normalizeOptionalText(this.diagnosticsForm.controls.channelCode.value),
      audienceCode: normalizeOptionalText(this.diagnosticsForm.controls.audienceCode.value),
      tenantId: normalizeOptionalText(this.diagnosticsForm.controls.tenantId.value),
    };
  }
}

function normalizeOptionalText(value: string | null | undefined): string | undefined {
  const normalized = value?.trim() ?? "";
  return normalized.length > 0 ? normalized : undefined;
}
